import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  CircularProgress,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import landingApi from "../../contexts/landingApiClient";

const getEventColor = (eventName) => {
  switch (eventName?.toLowerCase()) {
    case "location_added":
    case "subscription_paid":
      return "success";
    case "location_removed":
    case "subscription_cancelled":
      return "error";
    case "sign_up":
      return "info";
    default:
      return "default";
  }
};

const AffiliateEventsTable = ({
  affiliateId,
  pageSize: initialPageSize = 5,
}) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [error, setError] = useState(null);

  const fetchEvents = useCallback(async () => {
    if (!affiliateId) return;
    setIsFetching(true);
    try {
      const response = await landingApi.get("/affiliate-events", {
        params: {
          "filters[affiliate][id][$eq]": affiliateId,
          "pagination[page]": page + 1,
          "pagination[pageSize]": rowsPerPage,
          sort: "createdAt:desc",
        },
      });
      setEvents(response.data.data);
      if (response.data.meta && response.data.meta.pagination) {
        setPagination(response.data.meta.pagination);
      }
    } catch (err) {
      console.error(t("error_fetch_events"), err);
      setError(t("error_fetch_events"));
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [affiliateId, page, rowsPerPage, t]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSkeleton = loading && events.length === 0;

  return (
    <Paper sx={{ p: 3, position: "relative", mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t("activity_history")}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {isFetching && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("date")}</TableCell>
                  <TableCell>{t("event")}</TableCell>
                  <TableCell>{t("business_email")}</TableCell>
                  <TableCell>{t("description")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {showSkeleton ? (
                  Array.from(new Array(rowsPerPage)).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                    </TableRow>
                  ))
                ) : events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {t("no_events_found")}
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((event) => (
                    <TableRow key={event.id} hover>
                      <TableCell>
                        {new Date(event.attributes.createdAt).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={event.attributes.eventName?.replace(/_/g, " ") || "-"}
                          color={getEventColor(event.attributes.eventName)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {event.attributes.businessEmail || "-"}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Tooltip
                          title={event.attributes.description || ""}
                          arrow
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {event.attributes.description || "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("rows_per_page")}
          />
        </>
      )}
    </Paper>
  );
};

export default AffiliateEventsTable;
